import { ScrollView } from "react-native";
import { useLayoutEffect, useMemo } from "react";
import { useLocalSearchParams, useNavigation } from "expo-router";
import { addWeeks, startOfWeek } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import { View, Text, ScreenContainer, Card } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { WeeklySchedule, type ScheduleHistoryEntry } from "@src/features/schedules/components/WeeklySchedule";
import { useAlarm } from "@src/services/api/hooks";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { useAlarmScheduleHistory } from "@visioni/a3s-alarm-schedules/app";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import { toISODateString } from "@visioni/a3s-codecs";

export default function AppliedScheduleScreen() {
  const navigation = useNavigation();
  const { tenantId, siteId, cameraKey } = useLocalSearchParams<{
    tenantId: string;
    siteId: string;
    cameraKey: string;
  }>();
  const fallbackRoute =
    tenantId && siteId && cameraKey ? `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarm` : "/alarms";
  const { headerLeft } = useHeaderBackButton(fallbackRoute);
  const { data: alarm, isLoading, isFetching, error, refetch } = useAlarm(siteId, cameraKey);

  const alarmTimezone =
    alarm?.settings?.timezone ??
    (typeof alarm?.applied === "object" && alarm?.applied !== null ?
      alarm.applied.schedule?.metadata?.timezone
    : undefined) ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Fetch schedule history for the past 4 weeks, anchored to the alarm's timezone
  const historyRange = useMemo(() => {
    const nowInTz = toZonedTime(new Date(), alarmTimezone);
    const fromInTz = startOfWeek(addWeeks(nowInTz, -4), { weekStartsOn: 1 });
    return {
      from: toISODateString(fromZonedTime(fromInTz, alarmTimezone)),
      to: toISODateString(new Date()),
    };
  }, [alarmTimezone]);

  const { data: historyData, isLoading: isHistoryLoading } = useAlarmScheduleHistory({
    siteId: siteId ? SiteId.fromString(siteId) : (undefined as unknown as SiteId),
    assetCameraKey: cameraKey ? AssetCameraKey.fromString(cameraKey) : (undefined as unknown as AssetCameraKey),
    from: historyRange.from,
    to: historyRange.to,
    enabled: !!siteId && !!cameraKey,
  });

  const scheduleHistory: ScheduleHistoryEntry[] | undefined = useMemo(() => {
    if (!historyData?.data?.entries) return undefined;
    return historyData.data.entries.map((entry) => ({
      schedule: entry.schedule as unknown as AlarmSchedule,
      activeFrom: entry.activeFrom,
      activeUntil: entry.activeUntil,
    }));
  }, [historyData]);

  useLayoutEffect((): void => {
    navigation.setOptions({
      title: "Currently Applied Schedule",
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
    });
  }, [navigation, headerLeft]);

  // Check if alarm has applied schedule (for isEmpty check)
  const hasAppliedSchedule = (alarmData: NonNullable<typeof alarm>) =>
    !alarmData.applied || typeof alarmData.applied !== "object";

  // Render applied schedule content
  const renderAppliedScheduleContent = (alarmData: NonNullable<typeof alarm>) => {
    // This check is for type safety - DataStateView's isEmpty handles the empty state
    if (!alarmData.applied || typeof alarmData.applied !== "object") {
      return null;
    }

    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 40 }}>
        <View px="5xl" py="4xl" bg="background">
          <Card>
            <View mb="3xl">
              <Text textStyle="sm/medium" mb="xs">
                Currently Applied Schedule
              </Text>
              {alarmData.applied.at ?
                <Text textStyle="xs/regular" color="textMuted" mb="3xl">
                  Applied{" "}
                  {new Intl.DateTimeFormat(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(alarmData.applied.at))}
                </Text>
              : <View mb="3xl" />}
              <WeeklySchedule
                schedule={alarmData.applied.schedule as AlarmSchedule}
                exceptions={alarmData.settings?.exceptions ?? []}
                timezone={alarmTimezone}
                scheduleHistory={scheduleHistory}
                isHistoryLoading={isHistoryLoading}
              />
            </View>
          </Card>
        </View>
      </ScrollView>
    );
  };

  return (
    <ScreenContainer edges={[]}>
      <DataStateView
        data={alarm}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        isEmpty={hasAppliedSchedule}
        loadingMessage="Loading applied schedule..."
        errorTitle="Error Loading Schedule"
        errorMessage="Failed to load applied schedule."
        emptyState={{
          icon: "calendarOutline",
          title: "No Applied Schedule",
          message: "No schedule is currently applied to this camera.",
        }}
      >
        {renderAppliedScheduleContent}
      </DataStateView>
    </ScreenContainer>
  );
}
